/**
 * RuleTestPanel - Panel for testing categorization rules (WP-CAT-12)
 *
 * Allows users to enter sample text and see which rules would match.
 *
 * Features:
 * - Text input for sample transaction name
 * - Pattern type selector
 * - "Test" button to evaluate against all rules
 * - Display matching rules with priority/confidence
 * - Show which rule would win (best match)
 */
import { useState, useCallback } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { useTestCategorizationRules } from '@/hooks/useCategorizationRules';
import { useCategoryPreferences } from '@/hooks/useCategoryPreferences';
import { PATTERN_TYPE_CONFIG, type PatternType, type RuleTestResponse } from '@/types/categorizationRule';

// ============================================================================
// Props
// ============================================================================

export interface RuleTestPanelProps {
  onClose: () => void;
}

// ============================================================================
// Component
// ============================================================================

export default function RuleTestPanel({ onClose }: RuleTestPanelProps) {
  // Form state
  const [text, setText] = useState('');
  const [patternType, setPatternType] = useState<PatternType>('description');
  const [result, setResult] = useState<RuleTestResponse | null>(null);

  // Get categories for display
  const { data: categories } = useCategoryPreferences(true);

  // Test mutation
  const testMutation = useTestCategorizationRules();

  // Handle test
  const handleTest = useCallback(async () => {
    if (!text.trim()) return;

    try {
      const response = await testMutation.mutateAsync({
        text: text.trim(),
        patternType,
      });
      setResult(response);
    } catch {
      // Error is handled by React Query
    }
  }, [text, patternType, testMutation]);

  // Helper to get category name
  const getCategoryName = (categoryId: string): string => {
    const category = categories?.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Test Categorization Rules</h2>
          <p className="mt-1 text-sm text-gray-600">
            Enter sample text to see which rules would match.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[calc(80vh-200px)]">
          {/* Input fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="test-text" className="block text-sm font-medium text-gray-700 mb-1">
                Sample Text
              </label>
              <Input
                id="test-text"
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g., STARBUCKS #12345 SEATTLE WA"
              />
            </div>

            <div>
              <Select
                id="test-pattern-type"
                label="Pattern Type"
                value={patternType}
                onChange={(e) => setPatternType(e.target.value as PatternType)}
                options={Object.entries(PATTERN_TYPE_CONFIG).map(([key, config]) => ({
                  value: key,
                  label: config.label,
                }))}
              />
            </div>
          </div>

          {/* Test button */}
          <div>
            <Button
              variant="primary"
              onClick={handleTest}
              disabled={!text.trim() || testMutation.isPending}
              isLoading={testMutation.isPending}
            >
              Test Rules
            </Button>
          </div>

          {/* Loading state */}
          {testMutation.isPending && (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          )}

          {/* Error state */}
          {testMutation.isError && (
            <Alert variant="error">
              Failed to test rules. Please try again.
            </Alert>
          )}

          {/* Results */}
          {result && !testMutation.isPending && (
            <div className="space-y-4">
              {/* Best match */}
              {result.bestMatch ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800 mb-2">
                    Best Match
                  </h3>
                  <div className="text-sm text-green-700">
                    <p className="font-medium">{result.bestMatch.ruleName}</p>
                    <p>
                      Category: <strong>{getCategoryName(result.bestMatch.categoryId)}</strong>
                    </p>
                    <p>
                      Priority: {result.bestMatch.priority}, Confidence: {(result.bestMatch.confidence * 100).toFixed(0)}%
                    </p>
                    {result.bestMatch.isSystem && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 mt-1">
                        System Rule
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <Alert variant="warning">
                  No rules matched the sample text.
                </Alert>
              )}

              {/* All matches */}
              {result.matches.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    All Matching Rules ({result.matches.length})
                  </h3>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                    {result.matches.map((match) => (
                      <div key={match.ruleId} className="p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{match.ruleName}</span>
                          <div className="flex items-center gap-2">
                            {match.isSystem && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                System
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          <span>Category: {getCategoryName(match.categoryId)}</span>
                          <span className="mx-2">|</span>
                          <span>Priority: {match.priority}</span>
                          <span className="mx-2">|</span>
                          <span>Confidence: {(match.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
